import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';
import config from '../config';
import './Auth.css';

const RegisterPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  // Step 1: Email & Password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2: Personal Info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [district, setDistrict] = useState('');

  // Step 3: Pet Info (optional)
  const [addPet, setAddPet] = useState(false);
  const [petName, setPetName] = useState('');
  const [petSpecies, setPetSpecies] = useState('dog');
  const [petBreed, setPetBreed] = useState('');
  const [petGender, setPetGender] = useState('');
  const [petAge, setPetAge] = useState('');
  const [petWeight, setPetWeight] = useState('');

  const handleGoogleRegister = () => {
    const callback = `${window.location.origin}/`;
    window.location.href = `${config.API_BASE}/api/connect/google?callback=${encodeURIComponent(callback)}`;
  };

  const handleAppleRegister = () => {
    const callback = `${window.location.origin}/`;
    window.location.href = `${config.API_BASE}/api/connect/apple?callback=${encodeURIComponent(callback)}`;
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password || !confirmPassword) {
      setError('所有欄位為必填');
      return;
    }

    if (password !== confirmPassword) {
      setError('密碼不符');
      return;
    }

    if (password.length < 6) {
      setError('密碼至少 6 個字符');
      return;
    }

    setStep(2);
  };

  const handleStep2Submit = (e) => {
    e.preventDefault();
    setError('');

    if (!firstName || !lastName || !phone) {
      setError('姓名和電話為必填');
      return;
    }

    setStep(3);
  };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Register user
      const result = await register(email, password, firstName, lastName);
      if (!result.success) {
        setError(result.error);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');

      // Update profile
      await axiosInstance.put(
        '/auth/profile',
        {
          firstName,
          lastName,
          phone,
          gender: gender || undefined,
          age: age ? parseInt(age) : undefined,
          district: district || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add pet if selected
      if (addPet && petName) {
        await axiosInstance.post(
          '/pets/me',
          {
            name: petName,
            species: petSpecies,
            breed: petBreed || undefined,
            gender: petGender || undefined,
            age: petAge ? parseInt(petAge) : undefined,
            weight: petWeight ? parseFloat(petWeight) : undefined,
          }
        );
      }

      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || '註冊失敗');
    } finally {
      setLoading(false);
    }
  };

  const progressValue = (step / 3) * 100;

  return (
    <Container className="auth-container">
      <Card className="auth-card">
        <Card.Body>
          <Card.Title className="text-center mb-4">註冊帳戶</Card.Title>

          {step === 1 && (
            <div className="oauth-buttons mb-4" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              <Button
                variant="outline-secondary"
                className="w-100 mb-2"
                onClick={handleGoogleRegister}
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" className="oauth-icon" />
                使用 Google 帳戶註冊
              </Button>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={handleAppleRegister}
              >
                <span className="oauth-icon">🍎</span>
                使用 Apple 帳戶註冊
              </Button>
              <div className="text-center text-muted mt-3" style={{ fontSize: '0.9rem' }}>
                或使用電郵建立會員帳戶
              </div>
            </div>
          )}

          <ProgressBar now={progressValue} className="mb-4" label={`步驟 ${step} of 3`} />

          {error && <Alert variant="danger">{error}</Alert>}

          {/* Step 1: Email & Password */}
          {step === 1 && (
            <Form onSubmit={handleStep1Submit}>
              <Form.Group className="mb-3">
                <Form.Label>電郵</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>密碼</Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>確認密碼</Form.Label>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </Form.Group>

              <Button variant="primary" type="submit" className="w-100">
                下一步
              </Button>
            </Form>
          )}

          {/* Step 2: Personal Info */}
          {step === 2 && (
            <Form onSubmit={handleStep2Submit}>
              <Form.Group className="mb-3">
                <Form.Label>名字</Form.Label>
                <Form.Control
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>姓氏</Form.Label>
                <Form.Control
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>電話</Form.Label>
                <Form.Control
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>性別</Form.Label>
                <Form.Select value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="">選擇...</option>
                  <option value="male">男</option>
                  <option value="female">女</option>
                  <option value="other">其他</option>
                  <option value="prefer_not_to_say">不想說</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>年齡</Form.Label>
                <Form.Control
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>地區</Form.Label>
                <Form.Control
                  type="text"
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  placeholder="香港地區"
                />
              </Form.Group>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                >
                  下一步
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setStep(1)}
                >
                  上一步
                </Button>
              </div>
            </Form>
          )}

          {/* Step 3: Pet Info (Optional) */}
          {step === 3 && (
            <Form onSubmit={handleStep3Submit}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="現在新增寵物資料（可選）"
                  checked={addPet}
                  onChange={(e) => setAddPet(e.target.checked)}
                />
              </Form.Group>

              {addPet && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>寵物名字</Form.Label>
                    <Form.Control
                      type="text"
                      value={petName}
                      onChange={(e) => setPetName(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>種類</Form.Label>
                    <Form.Select value={petSpecies} onChange={(e) => setPetSpecies(e.target.value)}>
                      <option value="dog">狗</option>
                      <option value="cat">貓</option>
                      <option value="rabbit">兔</option>
                      <option value="bird">鳥</option>
                      <option value="hamster">倉鼠</option>
                      <option value="guinea_pig">豚鼠</option>
                      <option value="other">其他</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>品種</Form.Label>
                    <Form.Control
                      type="text"
                      value={petBreed}
                      onChange={(e) => setPetBreed(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>性別</Form.Label>
                    <Form.Select value={petGender} onChange={(e) => setPetGender(e.target.value)}>
                      <option value="">選擇...</option>
                      <option value="male">公</option>
                      <option value="female">母</option>
                      <option value="unknown">不詳</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>年齡</Form.Label>
                    <Form.Control
                      type="number"
                      value={petAge}
                      onChange={(e) => setPetAge(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>體重（公斤）</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={petWeight}
                      onChange={(e) => setPetWeight(e.target.value)}
                    />
                  </Form.Group>
                </>
              )}

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? '正在註冊...' : '完成註冊'}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setStep(2)}
                >
                  上一步
                </Button>
              </div>
            </Form>
          )}

          <div className="text-center mt-4">
            <p>
              已有帳戶？<Link to="/login">登入</Link>
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RegisterPage;
